import { keyframes } from '@emotion/react'
import { CreateFocusCss } from '../../../styles'
import type { Tokens } from '../../../theming/types'
import { styles } from '../../../utilities'
import type { ButtonProps } from '..'

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export default (config: {
  props: Pick<ButtonProps, 'animate' | 'disabled' | 'loading' | 'text'>
  tokens: Tokens
}) => {
  const { props, tokens } = config

  return {
    button: {
      default: {
        ...tokens.fontButton,
        alignItems: 'center',
        borderRadius: '2px',
        borderColor: 'transparent',
        borderStyle: 'solid',
        borderWidth: '1px',
        cursor: props.loading ? 'progress' : 'pointer',
        display: 'inline-flex',
        justifyContent: props.animate ? undefined : 'center',
        letterSpacing: '0.66px',
        margin: 0,
        minHeight: tokens.ctaSizeMd,
        overflow: 'visible',
        padding: '4px 14px',
        position: 'relative',
        textAlign: 'center',
        textDecoration: 'none',
        textTransform: 'uppercase',
        transitionProperty: 'background-color, border-color, box-shadow, color',
        transitionDuration: '0.1s',
        transitionTimingFunction: 'cubic-bezier(0.33, 0, 0.67, 1)',
        verticalAlign: 'middle',

        '&:lang(ja), &:lang(ko), &:lang(zh)': {
          whiteSpace: 'nowrap',
        },

        '& img': {
          height: '16px',
        },

        // This is for teams that use a version on the icons package before v4.22.1.
        // They do not have the fill="currentColor" attribute on the svg icons before v4.22.1.
        '& svg': {
          fill: 'currentColor',
        },

        ...CreateFocusCss(tokens.focusOuter),
      },

      groupStart: {
        borderRadius: '2px 0px 0px 2px',
        // Support for logical border-radius is not good. Thus, leave in above fallback.
        borderStartStartRadius: '2px',
        borderStartEndRadius: '0px',
        borderEndEndRadius: '0px',
        borderEndStartRadius: '2px',
        borderInlineEnd: 'none',

        ...CreateFocusCss({
          zIndex: 1,
        }),
      },

      groupCenter: {
        borderRadius: 0,
        borderInlineEnd: 'none',

        ...CreateFocusCss({
          zIndex: 1,
        }),
      },

      groupEnd: {
        borderRadius: '0px 2px 2px 0px',
        borderStartStartRadius: '0px',
        borderStartEndRadius: '2px',
        borderEndEndRadius: '2px',
        borderEndStartRadius: '0px',
        ...CreateFocusCss({
          zIndex: 1,
        }),
      },

      inverted: {
        ...CreateFocusCss({
          outlineColor: tokens.button.invertedBackgroundColor,
        }),

        secondary: {
          backgroundColor: 'transparent',
          borderColor: tokens.button.invertedBackgroundColor,
          color: tokens.button.invertedBackgroundColor,

          '& svg': {
            fill: tokens.button.invertedBackgroundColor,
          },

          '&:hover': {
            backgroundColor: tokens.button.invertedBackgroundColor,
            color: tokens.button.invertedTextColor,

            '& svg': {
              fill: tokens.button.invertedTextColor,
            },
          },

          '&:active': {
            backgroundColor: tokens.dark.ctaBgColorSecondaryActive,
            color: tokens.dark.fontColorDefault,
            transitionDuration: '0.15s',

            '& svg': {
              fill: tokens.button.invertedBackgroundColor,
            },
          },

          '&:disabled, &:disabled:hover': {
            opacity: 0.4,
          },
        },

        tertiary: {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          color: tokens.button.invertedBackgroundColor,

          '& svg': {
            fill: tokens.button.invertedBackgroundColor,
          },

          '&:hover': {
            borderColor: tokens.button.invertedBackgroundColor,
            color: tokens.button.invertedBackgroundColor,
            transition: `
            background-color 0.2s ease,
            border 0.2s ease,
            box-shadow 0.2s ease,
            color 0.2s ease
          `,

            '& svg': {
              fill: tokens.button.invertedBackgroundColor,
            },
          },

          '&:active, &[aria-expanded="true"]': {
            backgroundColor: tokens.dark.ctaBgColorTertiaryActive,
            color: tokens.dark.fontColorDefault,
            transitionDuration: '0.15s',

            '& svg': {
              fill: tokens.button.invertedBackgroundColor,
            },
          },

          '&:disabled, &:disabled:hover': {
            opacity: 0.4,
          },
        },
      },

      round: {
        borderRadius: '50%',
        boxShadow: tokens.elevationLow,
        height: '48px',
        minHeight: '48px',
        padding: 0,
        width: '48px',

        '&:hover': {
          boxShadow: tokens.elevationMedium,
        },
      },

      fullWidth: {
        width: '100%',
      },

      text: {
        pointerEvents: 'none',
      },
    },

    icon: {
      default: {
        lineHeight: '0.66px',
        pointerEvents: 'none',
      },

      round: {
        margin: 0,
      },

      hideText: {
        margin: 0,
      },
    },

    caret: {
      display: 'inline-flex',
    },

    hideText: {
      padding: 0,
    },

    hidden: styles.visuallyHidden,

    loadingSpinner: {
      animation: `${spin} 1s infinite linear`,
      borderRadius: '100%',
      borderStyle: 'solid',
      borderTopColor: 'transparent',
      borderWidth: '2px',
      display: 'inline-block',
      flexShrink: 0,
      height: '16px',
      marginInline: '4px',
      marginInlineEnd: props.text ? '12px' : undefined,
      verticalAlign: 'middle',
      width: '16px',
    },

    animation: {
      display: 'inline-flex',
      justifyContent: 'flex-start',
      width: '100%',

      '&[data-animating="true"]': {
        justifyContent: 'center',
      },

      inner: {
        alignItems: 'center',
        display: 'inline-flex',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  } as const
}
